import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from '@/store/store'
import { selectTurnIdForArtifactVersion } from '@/artifacts/store/selectors'
import { useFootnoteInfoByCitationId } from '@/agent/references/hooks/useFootnoteInfoByCitationId'
import { selectAgentConversationMatterId } from '@/agent/conversations/store/selectors'
import { buildReferenceInfoByCitationId, type ReferenceInfoByCitationId } from '@/artifact-editor/utils/citation-helpers'

type UseReferenceInfoOptions = {
  includeLegacyFileViewerUrls?: boolean
}

/**
 * Citation-rendering info for an artifact version. While references are still loading
 * the map is empty, so unknown citations render stripped/plain and fill in when the
 * conversation payload arrives.
 */
export function useReferenceInfo(artifactVersionId: string, conversationId: string, options: UseReferenceInfoOptions = {}): ReferenceInfoByCitationId {
  const turnId = useSelector((state: RootState) => selectTurnIdForArtifactVersion(state, { artifactVersionId }))
  const footnoteInfoByCitationId = useFootnoteInfoByCitationId(turnId)
  const allReferences = useSelector((state: RootState) => state.agentReferencesState.references)
  const matterId = useSelector((state: RootState) => selectAgentConversationMatterId(state, { conversationId }))
  const includeLegacyFileViewerUrls = options.includeLegacyFileViewerUrls ?? true

  return useMemo(
    () => buildReferenceInfoByCitationId(footnoteInfoByCitationId, allReferences, matterId, { includeLegacyFileViewerUrls }),
    [footnoteInfoByCitationId, allReferences, matterId, includeLegacyFileViewerUrls]
  )
}
